import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiChevronLeft, FiChevronRight, FiPackage, FiTag, FiLayers } from "react-icons/fi";
import axios from "../../../Utils/axiosInstance";

const FeaturedVarieties = () => {
  const scrollRef = useRef(null);
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchVarieties = async () => {
      try {
        setLoading(true);
        setError(false);

        const { data } = await axios.get("/product-variety/get-all", {
          params: { populate: "true", isActive: "true" },
        });

        setVarieties(data?.data || []);
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchVarieties();
  }, []);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir === "left" ? -320 : 320,
      behavior: "smooth",
    });
  };

  const getVarietyHref = (variety) => {
    const params = new URLSearchParams();

    const categoryId =
      variety?.productId?.subCategoryId?.categoryId?._id ||
      variety?.productId?.subCategoryId?.categoryId ||
      "";

    const subCategoryId =
      variety?.productId?.subCategoryId?._id || variety?.subCategoryId || "";

    const productId = variety?.productId?._id || variety?.productId || "";

    if (categoryId) params.set("category", categoryId);
    if (subCategoryId) params.set("subCategory", subCategoryId);
    if (productId) params.set("product", productId);
    if (variety?._id) params.set("variety", variety._id);

    return `/products${params.toString() ? `?${params.toString()}` : ""}`;
  };

  return (
    <section className="w-full pb-14 md:pb-16 px-4 md:px-8">
      <div className="2xl:max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor py-2">
            Featured Product Varieties
          </h2>
          <p className="text-xs md:w-[70%] mx-auto md:text-sm text-gray-500 mt-2 leading-relaxed">
            Explore our most popular product varieties and jump straight into the right category, sub-category, and product selection flow.
          </p>
        </div>

        {loading && (
          <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="min-w-[85%] md:min-w-[19rem] lg:min-w-[20.5rem] 2xl:min-w-[19rem] max-w-[19rem] flex-shrink-0 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm animate-pulse snap-start"
              >
                <div className="h-44 rounded-2xl bg-gray-200" />
                <div className="mt-4 h-4 w-3/4 rounded bg-gray-200" />
                <div className="mt-3 h-3 w-1/2 rounded bg-gray-200" />
                <div className="mt-4 h-10 rounded-xl bg-gray-100" />
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-3xl border border-dashed border-gray-300 bg-white/80 py-12 text-center">
            <p className="text-base font-semibold text-gray-700">Unable to load varieties right now</p>
            <p className="mt-2 text-sm text-gray-500">Please refresh the page and try again.</p>
          </div>
        )}

        {!loading && !error && varieties.length === 0 && (
          <div className="rounded-3xl border border-dashed border-gray-300 bg-white/80 py-12 text-center">
            <p className="text-base font-semibold text-gray-700">No varieties available</p>
            <p className="mt-2 text-sm text-gray-500">We will showcase new product varieties here soon.</p>
          </div>
        )}

        {!loading && !error && varieties.length > 0 && (
          <>
            <div
              ref={scrollRef}
              className="flex gap-4 md:gap-5 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth"
            >
              {varieties.map((variety) => {
                const image = variety?.img;
                const productName = variety?.productId?.name || "Product";
                const categoryName =
                  variety?.productId?.subCategoryId?.categoryId?.name ||
                  variety?.productId?.subCategoryId?.name ||
                  "Category";

                return (
                  <Link
                    key={variety._id}
                    to={getVarietyHref(variety)}
                    className="group min-w-[85%] md:min-w-[19rem] lg:min-w-[20.5rem] 2xl:min-w-[19rem] max-w-[19rem] flex-shrink-0 snap-start rounded-[1.5rem] border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-primaryColor/30"
                  >
                    <div className="relative overflow-hidden rounded-[1.25rem] m-3 mb-0 bg-gradient-to-br from-[#FFF7F7] via-white to-[#FFEDEE]">
                      {image ? (
                        <img
                          src={image}
                          alt={variety?.varietyName || productName}
                          className="h-48 w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-48 w-full items-center justify-center bg-gradient-to-br from-[#FFF7F7] to-[#FFEDEE]">
                          <div className="flex flex-col items-center justify-center rounded-full border border-white/70 bg-white/70 p-5 text-primaryColor shadow-sm">
                            <FiPackage className="text-3xl" />
                          </div>
                        </div>
                      )}

                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                      <div className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primaryColor shadow-sm">
                        {categoryName}
                      </div>
                    </div>

                    <div className="p-4 sm:p-5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-primaryColor/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primaryColor">
                          <FiTag className="text-[12px]" />
                          {variety?.grade || "Premium"}
                        </span>
                        <span className="text-xs font-medium text-gray-500">
                          {productName}
                        </span>
                      </div>

                      <h3 className="mt-3 text-lg flex gap-3 items-center font-semibold text-gray-900 line-clamp-2">
                        {variety?.varietyName || "Product Variety"} <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                          <span className="rounded-full bg-gray-100 px-2 py-1">
                            MOQ {(variety?.slabAdjustedMOQ ?? variety?.minimum_MOQ) || 0} {variety?.standardizedUNIT || "unit"}
                          </span>
                        </div>
                      </h3>
                      <div className="mt-3 flex items-center justify-between rounded-2xl border border-gray-100 bg-[#FFF9FA] px-3 py-2">
                        <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <FiLayers className="text-primaryColor" />
                          Explore variety
                        </span>
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primaryColor text-white transition duration-300 group-hover:translate-x-1">
                          <FiArrowRight />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => scroll("left")}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-primaryBtnColor text-white transition hover:bg-primaryBtnHoverColor"
              >
                <FiChevronLeft />
              </button>

              <Link
                to="/all-varieties"
                className="rounded-full border border-primaryColor bg-white px-5 py-2 text-sm font-semibold text-primaryColor transition hover:bg-primaryColor hover:text-white"
              >
                View All
              </Link>

              <button
                onClick={() => scroll("right")}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-primaryBtnColor text-white transition hover:bg-primaryBtnHoverColor"
              >
                <FiChevronRight />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default FeaturedVarieties;
